import { CheckCircle, XCircle, AlertTriangle, X } from 'lucide-react';
import { useState, useEffect } from 'react';

export type ToastType = 'success' | 'error' | 'warning';

export interface Toast {
  id: string;
  type: ToastType;
  title: string;
  message?: string;
  duration?: number;
  showRetry?: boolean;
  onRetry?: () => void;
}

export function ToastContainer({ toasts, onDismiss }: { 
  toasts: Toast[]; 
  onDismiss: (id: string) => void;
}) {
  return (
    <div className="fixed top-4 right-4 z-50 flex flex-col gap-2 max-w-md">
      {toasts.map((toast) => (
        <ToastItem 
          key={toast.id} 
          toast={toast} 
          onDismiss={onDismiss}
        />
      ))}
    </div>
  );
}

function ToastItem({ toast, onDismiss }: { toast: Toast; onDismiss: (id: string) => void }) {
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    if (toast.duration && toast.duration > 0) {
      const timer = setTimeout(() => {
        handleDismiss();
      }, toast.duration);
      return () => clearTimeout(timer);
    }
  }, [toast.duration]);

  const handleDismiss = () => {
    setIsExiting(true);
    setTimeout(() => {
      onDismiss(toast.id);
    }, 300);
  };

  const getToastStyles = () => {
    switch (toast.type) {
      case 'success':
        return {
          bg: 'bg-emerald-50 border-emerald-200',
          icon: <CheckCircle className="w-5 h-5 text-emerald-600" />,
          titleColor: 'text-emerald-900',
          messageColor: 'text-emerald-700'
        };
      case 'error':
        return {
          bg: 'bg-red-50 border-red-200',
          icon: <XCircle className="w-5 h-5 text-red-600" />,
          titleColor: 'text-red-900',
          messageColor: 'text-red-700'
        };
      case 'warning':
        return {
          bg: 'bg-amber-50 border-amber-200',
          icon: <AlertTriangle className="w-5 h-5 text-amber-600" />,
          titleColor: 'text-amber-900',
          messageColor: 'text-amber-700'
        };
    }
  };

  const styles = getToastStyles();

  return (
    <div 
      className={`${styles.bg} border rounded-lg shadow-lg p-4 min-w-[320px] transition-all duration-300 ${
        isExiting ? 'opacity-0 translate-x-8' : 'opacity-100 translate-x-0'
      }`}
    >
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 mt-0.5">
          {styles.icon}
        </div>
        
        <div className="flex-1 min-w-0">
          <h4 className={`text-sm font-semibold ${styles.titleColor} mb-1`}>
            {toast.title}
          </h4>
          {toast.message && (
            <p className={`text-sm ${styles.messageColor}`}>
              {toast.message}
            </p>
          )}
          
          {toast.showRetry && toast.onRetry && (
            <button 
              onClick={toast.onRetry}
              className="mt-2 text-sm font-medium underline hover:no-underline"
            >
              Retry
            </button>
          )}
        </div>
        
        <button 
          onClick={handleDismiss}
          className="flex-shrink-0 text-slate-400 hover:text-slate-600 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

export function useToast() {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const addToast = (toast: Omit<Toast, 'id'>) => {
    const id = Date.now().toString();
    const duration = toast.duration !== undefined ? toast.duration : (toast.type === 'success' ? 3000 : 5000);
    setToasts((prev) => [...prev, { ...toast, id, duration }]);
  };

  const dismissToast = (id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  };

  const success = (title: string, message?: string, duration?: number) => {
    addToast({ type: 'success', title, message, duration });
  };

  const error = (title: string, message?: string, showRetry?: boolean, onRetry?: () => void) => {
    addToast({ type: 'error', title, message, showRetry, onRetry });
  };

  const warning = (title: string, message?: string, duration?: number) => {
    addToast({ type: 'warning', title, message, duration });
  };

  return {
    toasts,
    dismissToast,
    success,
    error,
    warning
  };
}
